/*
	Manages a mechanism to handle uncaught exceptions.
*/

import { elasticLogException } from './ElasticLog.js';
import { getShared, p2 } from './Utils.js';
const tracking = require('promise/setimmediate/rejection-tracking');

let initialized = false;

const injectErrs = false; //NOTE: must be checked in as false

/*
 * This routine puts in place the platform-appropriate top-level error handlers,
 * thereby enabling the logging of errors to elasticSearch.
 */
function startErrorHandling() {
  if (initialized) {
    console.log('startErrorHandling called redundantly, ignoring');
    return;
  }
  initialized = true;

  getShared()['catalogError'] = catalogError;

  // if(Platform.OS == 'ios') {
  // 	tracking.enable({
  // 	  allRejections: true,
  // 	  onUnhandled: function(id, error) {
  // 	  	try {
  // 		  	elasticLogException({message: error.message});
  // 		  	catalogError(error.message);
  // 	  	}
  // 	  	catch(err) {
  // 	  		//log and snuff
  // 	  		console.log("Error while in onUnhandled: " + err);
  // 	  	}
  // 	  },
  // 	  onHandled: function() {
  // 	  	try {
  // 		  	elasticLogException({message: 'onHandled error'});
  // 		  	catalogError('onHandled error');
  // 	  	}
  // 	  	catch(err) {
  // 	  		//log and snuff
  // 	  		console.log("Error while in onHandled: " + err);
  // 	  	}
  // 	  }
  // 	});
  // }
  // else {
  window.onerror = function (message, url, line, col, error) {
    console.log('onError', message);
    elasticLogException({ message });
    catalogError(message);
  };
  window.addEventListener('unhandledrejection', function (event) {
    console.log('unhandledRejection', event.reason.message);
    elasticLogException({ message: '' + event.reason.message });
    catalogError(event.reason.message);
  });
  // }
}

const errBuckets = [];
const errorHistory = [];
const maxErrorHistory = 50; //the maximum number of errors we'll keep in our history
const MAX_HISTORY_REVEAL = 4; //arbitrary; max number of collated errors we'll inform listener clients of
const MAX_ERR_WINDOW_MINUTES = 10; //arbitrary; goal is to let ops notice errors, but not permanently
let mostRecentErrorString = '';
let autoHousekeepingStarted = false;

/*
 * Removes error-count buckets if they're older than our cutoff.
 */
function _discardOldBuckets() {
  let changed = false;
  let minute = _getMinute();
  while (errBuckets.length && errBuckets[0].minute < minute - MAX_ERR_WINDOW_MINUTES) {
    errBuckets.shift();
    changed = true;
  }
  return changed;
}

/*
 * @return an integer denoting the number of minutes since the epoch.
 */
function _getMinute() {
  return Math.floor(Date.now() / (1000 * 60));
}

/*
 * This returns a structure of the form { count, errStrings } where
 *
 *     - count is an integer of how many error have occured in the window for which we retain
 *        counts.
 *     - errStrings is an array of up to MAX_HISTORY_REVEAL distinct error strings, sorted
 *        most-to-least recent.
 *
 * @return the above structure if there is a non-zero number of errors, else returns
 *    undefined if there are no errors.
 */
function _getClientVisibleState() {
  let count = errBuckets.reduce((result, bucket) => result + bucket.count, 0);
  if (!count) return undefined;

  let distinctErrors = {};
  let cutoffTicks = Date.now() - MAX_ERR_WINDOW_MINUTES * 60 * 1000;
  errorHistory.forEach((historyRec) => {
    let { ticks, errorString } = historyRec;
    if (ticks < cutoffTicks) return;
    let entry = (distinctErrors[errorString] = distinctErrors[errorString] || { count: 0, errorString, ticks });
    ++entry.count;
    entry.ticks = Math.max(ticks, entry.ticks); //mark with the time of the most recent occurrence
  });
  let keys = Object.keys(distinctErrors);
  keys.sort((a, b) => distinctErrors[b].ticks - distinctErrors[a].ticks); //sort most-to-least recent
  const now = Date.now();
  let errStrings = keys.slice(0, MAX_HISTORY_REVEAL).map((key) => {
    let entry = distinctErrors[key];
    let secondsAgo = Math.floor((now - entry.ticks) / 1000);
    let minutesAgo = Math.floor(secondsAgo / 60);
    secondsAgo -= minutesAgo * 60;
    let agoStr = `${minutesAgo}:${p2(secondsAgo)}`;
    return `${entry.errorString} (${entry.count}, ${agoStr})`;
  });
  return { count, errStrings };
}

/*
 * Notifies all listeners of the current client-visible error status produced by
 * _getClientVisibleState.
 */
function _notifyAllListeners() {
  let clientVisibleState = _getClientVisibleState();
  errCountListeners.forEach((f) => _notifyOneListener(f, clientVisibleState));
}

/*
 * Notifies the specified listener of the current client-visible error status produced by _getClientVisibleState.
 *
 * @param listener is a function that takes the structure produced by _getClientVisibleState.
 */
function _notifyOneListener(listener, clientVisibleState) {
  listener(clientVisibleState);
}

/*
 * A new error has happened, tally its existence in our records and notify listeners of the
 * change.
 *
 * @param errorString is a string describing the error being cataloged.
 */
function catalogError(errorString) {
  mostRecentErrorString = errorString;

  errorHistory.unshift({ ticks: Date.now(), errorString });
  while (errorHistory.length > maxErrorHistory) errorHistory.pop();

  _discardOldBuckets();

  //add a count to the appropriate bucket
  let minute = _getMinute();
  let bucket;
  if (errBuckets.length) {
    bucket = errBuckets[errBuckets.length - 1];
    if (bucket.minute != minute) bucket = undefined;
  }
  if (!bucket) {
    bucket = { minute, count: 0 };
    errBuckets.push(bucket);
  }

  ++bucket.count;
  _notifyAllListeners();

  if (!autoHousekeepingStarted) {
    //now that we have at least one error, do periodic housekeeping
    autoHousekeepingStarted = true;

    function doHousekeeping() {
      if (_discardOldBuckets()) _notifyAllListeners();
      setTimeout(doHousekeeping, 1000 * 30);
    }
    setTimeout(doHousekeeping, 1000 * 30);
  }
}

/*
 * Add the supplied listenerFunc to the list of functions notified of changes to the recent
 * error count.
 *
 * @param listenerFunc is a function that takes the structure produced by
 *    _getClientVisibleState(), which will be undefined if there are no recent errors.
 * @return a function that de-registers the listener.
 */
function registerErrorStatusListener(listenerFunc) {
  errCountListeners.push(listenerFunc);

  //do a delayed notification of current status
  setTimeout(() => {
    _discardOldBuckets();
    if (errCountListeners.indexOf(listenerFunc) >= 0)
      //could have been deregistered
      _notifyOneListener(listenerFunc, _getClientVisibleState());
  }, 500);

  //return a deregisterer
  return () => {
    errCountListeners = errCountListeners.filter((f) => f !== listenerFunc);
  };
}

let errCountListeners = [];

export { startErrorHandling, registerErrorStatusListener, injectErrs };
